/*
 * Offer category landing page — /offers/<type>  e.g. /offers/electronics
 *
 * WHY THIS PAGE EXISTS
 *   Until this existed, /offers/electronics was a 404. App.js only matched
 *   /offers/:typeSlug/:slug (two segments), so every single-segment category
 *   URL fell through to NotFound and served "noindex, nofollow".
 *
 *   That cost two things. First, the head terms this site should rank for are
 *   category terms ("electronics offers today", "fashion deals india"), and
 *   there was no page to rank — only 332 individual offer pages and one
 *   undifferentiated /offers list. Second, the /offers filter bar is a row of
 *   <button>s that mutate React state, so a crawler could not walk from the
 *   listing into a category at all; the only path to an offer page was the
 *   sitemap. Google Search Console showed the result: 278 URLs "Discovered -
 *   currently not indexed", never fetched.
 *
 * The editorial copy per category lives in src/constants/categoryMeta.js —
 * see that file for why a bare grid of offer cards is not enough on its own.
 *
 * Deliberately does NOT use OffersContext: that context is the shared state
 * behind the home page and /offers, and driving it from here would clobber the
 * user's filter and scroll position on those pages. A local fetch also keeps
 * this page's prerender independent of them.
 */

import { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import { API_ENDPOINTS } from '../api';
import { OFFER_CATEGORIES } from '../constants/categories';
import { CATEGORY_META, displayLabel } from '../constants/categoryMeta';
import { KNOWN_STORES, offerTypeFromSlug, isKnownOfferTypeSlug, toSlug } from '../utils/slug';
import { MIN_CATEGORY_OFFERS_FOR_INDEX, ROBOTS_INDEXABLE, ROBOTS_NOINDEX } from '../utils/indexable';
import { useWishlist } from '../utils/wishlist';
import OfferCard from '../components/common/OfferCard';
import NotFound from './NotFound';

const SITE = 'https://www.offerlelo.com';

const FIRST_PAGE_SIZE = 6;
const NEXT_PAGE_SIZE = 12;

// Keyed by slug so each category page survives a remount (back navigation).
const categoryPageCache = new Map();

export default function OfferCategoryPage() {
  const { typeSlug } = useParams();
  const navigate = useNavigate();
  const { savedIds, toggleSave } = useWishlist();

  const slug = (typeSlug || '').toLowerCase();
  const known = isKnownOfferTypeSlug(slug);
  const categoryName = offerTypeFromSlug(slug);
  const meta = CATEGORY_META[categoryName];
  const cached = categoryPageCache.get(slug);

  const [items, setItems] = useState(() => cached?.items ?? []);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState(() => cached?.lastEvaluatedKey ?? null);
  const [hasMore, setHasMore] = useState(() => cached?.hasMore ?? true);
  const [loading, setLoading] = useState(() => !cached);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const sentinelRef = useRef(null);
  const hadCacheOnMount = useRef(!!cached);

  // Persist to cache on unmount so back-nav restores the list and scroll.
  useEffect(() => {
    return () => {
      if (known) categoryPageCache.set(slug, { items, lastEvaluatedKey, hasMore });
    };
  });

  useEffect(() => {
    if (!known) return;
    if (hadCacheOnMount.current) {
      hadCacheOnMount.current = false;
      return;
    }
    setItems([]);
    setLastEvaluatedKey(null);
    setHasMore(true);
    fetchCategoryOffers(null, true);
  }, [slug]);

  async function fetchCategoryOffers(lek, replace = false) {
    replace ? setLoading(true) : setLoadingMore(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        limit: String(replace ? FIRST_PAGE_SIZE : NEXT_PAGE_SIZE),
        // The API filters on the DISPLAY name, not the slug — the same param
        // the /offers filter bar sends. src/prerenderFetch.js honours it too,
        // so this page fills in at build time without touching the live API.
        category: categoryName,
      });
      if (lek) params.set('lastEvaluatedKey', JSON.stringify(lek));
      const response = await fetch(`${API_ENDPOINTS.OFFERS}?${params}`);
      if (!response.ok) throw new Error('Failed to fetch offers');
      const data = await response.json();
      const list = data.offers || data;
      const incoming = Array.isArray(list) ? list.filter(Boolean) : [];
      setItems(prev => {
        if (replace) return incoming;
        // Dedupe against what we already hold, in case pages overlap.
        const seen = new Set(prev.filter(i => i.id != null).map(i => i.id));
        return [...prev, ...incoming.filter(i => i.id == null || !seen.has(i.id))];
      });
      setLastEvaluatedKey(data.lastEvaluatedKey || null);
      setHasMore(!!data.lastEvaluatedKey);
    } catch (err) {
      setError(err.message);
    } finally {
      replace ? setLoading(false) : setLoadingMore(false);
    }
  }

  useEffect(() => {
    if (!sentinelRef.current) return;

    function maybeLoadMore() {
      if (!hasMore || loadingMore || loading) return;
      const el = sentinelRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // Same 600px look-ahead the observer uses, checked directly against the
      // viewport. A short first page (6 items) can leave the sentinel already
      // inside the viewport at mount, and IntersectionObserver only reports a
      // *change* in intersection — it never fires again once loading flips
      // from true to false if the element was intersecting the whole time, so
      // the list gets stuck at page one. This runs the same check directly.
      if (rect.top <= window.innerHeight + 600) {
        fetchCategoryOffers(lastEvaluatedKey);
      }
    }

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchCategoryOffers(lastEvaluatedKey);
        }
      },
      { threshold: 0, rootMargin: '600px 0px' }
    );
    observer.observe(sentinelRef.current);

    let ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        maybeLoadMore();
        ticking = false;
      });
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    // Run once immediately in case the sentinel is already in view.
    maybeLoadMore();

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, [hasMore, loadingMore, loading, lastEvaluatedKey]);

  // An unknown slug is a genuine 404 — /offers/foo must not render a hub.
  // Checked after the hooks above so hook order stays stable across renders.
  if (!known || !meta) return <NotFound />;

  const pageUrl = `${SITE}/offers/${slug}`;
  const label = displayLabel(categoryName);
  const pageTitle = `${label} Offers Today — Latest Deals & Discounts | OfferLelo`;
  const pageDesc = meta.intro.slice(0, 155);

  // Robots: only index a hub that actually holds enough offers to be worth a
  // visit — see MIN_CATEGORY_OFFERS_FOR_INDEX in src/utils/indexable.js.
  // `hasMore` counts as enough: it means the API has further pages, so the
  // category is larger than the first page we fetched.
  //
  // While the fetch is still in flight we emit no robots tag at all rather
  // than guess, so a slow response can never bake a wrong `noindex` into the
  // prerendered HTML. react-snap snapshots after the network goes idle, so the
  // built pages always carry a real verdict.
  const enoughOffers = items.length >= MIN_CATEGORY_OFFERS_FOR_INDEX || hasMore;
  const robots = error || !enoughOffers ? ROBOTS_NOINDEX : ROBOTS_INDEXABLE;
  const otherCategories = OFFER_CATEGORIES.filter(c => c !== categoryName);

  return (
    <div className="App">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDesc} />
        <meta name="keywords" content={meta.keywords} />
        {!loading && <meta name="robots" content={robots} />}
        <meta name="geo.region" content="IN" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDesc} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={`${SITE}/logo192.png`} />
        <meta property="og:site_name" content="OfferLelo" />
        <meta property="og:locale" content="en_IN" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDesc} />
        <meta name="twitter:image" content={`${SITE}/logo192.png`} />
        <link rel="canonical" href={pageUrl} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${label} Offers — Latest Deals & Discounts`,
          url: pageUrl,
          description: pageDesc,
          inLanguage: 'en-IN',
          isPartOf: { '@type': 'WebSite', url: SITE, name: 'OfferLelo' },
          about: { '@type': 'Thing', name: `${label} Offers` },
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: SITE },
              { '@type': 'ListItem', position: 2, name: 'Offers', item: `${SITE}/offers` },
              { '@type': 'ListItem', position: 3, name: `${label} Offers`, item: pageUrl },
            ],
          },
        })}</script>
      </Helmet>

      <header className="modern-header">
        <div className="header-logo" style={{ cursor: 'pointer' }} onClick={() => navigate('/')}>
          <img src="/logo.svg" alt="OfferLelo Logo" />
          <span className="header-logo-text">OfferLelo</span>
        </div>
        <div className="header-actions">
          <button className="admin-btn" onClick={() => navigate('/offers')}>&larr; All Offers</button>
        </div>
      </header>

      {/* Crawlable breadcrumb — also the return path a crawler follows back
          up to /offers, which matters because this page is often the entry. */}
      <nav aria-label="Breadcrumb" style={{ maxWidth: 1400, margin: '0 auto', padding: '14px 20px 0', fontSize: '0.85rem', color: '#64748b' }}>
        <a href="/" style={{ color: '#6366f1', textDecoration: 'none' }}>Home</a>
        <span style={{ margin: '0 6px' }}>/</span>
        <a href="/offers" style={{ color: '#6366f1', textDecoration: 'none' }}>Offers</a>
        <span style={{ margin: '0 6px' }}>/</span>
        <span>{categoryName}</span>
      </nav>

      <section
        style={{
          background: `linear-gradient(135deg, ${meta.color} 0%, ${meta.accent} 100%)`,
          padding: '44px 20px 38px',
          margin: '14px 0 0',
          color: '#fff',
          textAlign: 'center',
        }}
      >
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <div style={{
            display: 'inline-block', background: 'rgba(255,255,255,0.18)', padding: '6px 14px',
            borderRadius: 999, fontSize: '0.82rem', fontWeight: 600, marginBottom: 12, letterSpacing: 0.4,
          }}>
            {categoryName.toUpperCase()} CATEGORY
          </div>
          <h1 style={{ fontSize: 'clamp(1.6rem, 4vw, 2.4rem)', fontWeight: 800, margin: '0 0 10px', lineHeight: 1.2 }}>
            {label} Offers &mdash; Today&apos;s Latest Deals &amp; Discounts
          </h1>
          <p style={{ fontSize: '1.02rem', opacity: 0.95, margin: '0 auto', maxWidth: 720, lineHeight: 1.55 }}>
            {meta.tagline}
          </p>
        </div>
      </section>

      <section style={{ maxWidth: 900, margin: '28px auto 8px', padding: '0 20px' }}>
        <p style={{ fontSize: '0.98rem', color: '#475569', lineHeight: 1.7, margin: 0 }}>
          {meta.intro}
        </p>
        <ul style={{ marginTop: 16, paddingLeft: 22, color: '#475569', lineHeight: 1.8, fontSize: '0.95rem' }}>
          {meta.highlights.map(h => <li key={h}>{h}</li>)}
        </ul>
      </section>

      <h2 style={{ maxWidth: 1400, margin: '32px auto 12px', padding: '0 20px', fontSize: '1.3rem', color: '#1e293b' }}>
        Latest {label} Offers
      </h2>

      <div style={{ maxWidth: 1400, margin: '0 auto', padding: '0 12px 60px' }}>
        {loading && (
          <div className="grid-container">
            {Array.from({ length: 8 }).map((_, i) => (
              <div className="skeleton-card" key={i}>
                <div className="skeleton skeleton-card-img" />
                <div className="skeleton-card-body">
                  <div className="skeleton skeleton-card-line wide" />
                  <div className="skeleton skeleton-card-line mid" />
                  <div className="skeleton skeleton-card-line short" />
                </div>
              </div>
            ))}
          </div>
        )}
        {error && !loading && (
          <div style={{ color: '#ef4444', textAlign: 'center', padding: 20 }}>Error: {error}</div>
        )}
        {!loading && !error && (
          <>
            <div className="grid-container">
              {items.length === 0 ? (
                <div className="no-items">
                  No {label.toLowerCase()} offers right now &mdash; check back soon.
                </div>
              ) : (
                items.map(item => (
                  <OfferCard
                    key={item.id}
                    item={item}
                    savedIds={savedIds}
                    onToggleSave={toggleSave}
                  />
                ))
              )}
            </div>
            <div ref={sentinelRef} style={{ height: 32 }} />
            {loadingMore && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: meta.color, fontWeight: 600, fontSize: '0.95rem' }}>
                Loading more {label.toLowerCase()} offers...
              </div>
            )}
          </>
        )}

        {/* Real <a> links, not buttons: this is the crawl path between hubs.
            The /offers filter bar is buttons only, which is why crawlers could
            never reach a category before this page existed. */}
        <section style={{ marginTop: 48, padding: '24px 20px', background: '#f8fafc', borderRadius: 12 }}>
          <h2 style={{ fontSize: '1.1rem', color: '#1e293b', margin: '0 0 14px' }}>Browse Other Categories</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {otherCategories.map(c => {
              const cMeta = CATEGORY_META[c] || {};
              return (
                <a
                  key={c}
                  href={`/offers/${toSlug(c)}`}
                  style={{
                    background: '#fff', border: `1.5px solid ${cMeta.color || '#6366F1'}40`,
                    color: cMeta.color || '#6366F1', padding: '10px 18px', borderRadius: 999,
                    fontWeight: 600, fontSize: '0.92rem', textDecoration: 'none',
                  }}
                >
                  {displayLabel(c)} Offers &rarr;
                </a>
              );
            })}
          </div>

          <h2 style={{ fontSize: '1.1rem', color: '#1e293b', margin: '26px 0 14px' }}>Shop {label} Offers by Store</h2>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {KNOWN_STORES.map(s => (
              <a
                key={s}
                href={`/store/${s.toLowerCase()}`}
                style={{
                  background: '#fff', border: '1.5px solid #cbd5e1', color: '#334155',
                  padding: '10px 18px', borderRadius: 999, fontWeight: 600,
                  fontSize: '0.92rem', textDecoration: 'none',
                }}
              >
                {s} Offers &rarr;
              </a>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}
